//libraries
import React, { useState, useEffect, useCallback } from 'react';

//===============================
// SimpleFormatPainter Component
//===============================

const FormatPainterButton = ({ editor }: { editor: any }) => {
  //states
  const [isActive, setIsActive] = useState(false);
  const [copiedFormat, setCopiedFormat] = useState<any>(null);

  //copy selected text format
  const copyFormat = useCallback(() => {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    if (from === to) return;

    const marks = editor.state.selection.$anchor.marks();
    const node = editor.state.selection.$anchor.parent;

    setCopiedFormat({
      marks: marks.map((mark: any) => ({
        type: mark.type.name,
        attrs: mark.attrs
      })),
      nodeType: node.type.name,
      nodeAttrs: node.attrs
    });

    setIsActive(true);
  }, [editor]);

  // apply format (using tiptap commands)
  const applyFormatSimple = useCallback(() => {
    if (!editor || !copiedFormat) return;

    const { from, to } = editor.state.selection;
    if (from === to) return;

    // Check current context
    const isInTable = editor.isActive('table');
    const isInList = editor.isActive('bulletList') || editor.isActive('orderedList');

    // Start a single transaction
    const chain = editor.chain().focus();

    if (isInTable || isInList) {
      // Only apply text formatting in tables and lists
      chain.unsetAllMarks();
      copiedFormat.marks.forEach((markData: any) => {
        chain.setMark(markData.type, markData.attrs);
      });
    } else {
      // Apply full formatting in regular contexts
      
      // Apply block formatting first
      if (copiedFormat.nodeType === 'heading' && copiedFormat.nodeAttrs?.level) {
        chain.setHeading({ level: copiedFormat.nodeAttrs.level });
      } else if (copiedFormat.nodeType === 'blockquote') {
        chain.setBlockquote();
      } else if (copiedFormat.nodeType === 'bulletList') {
        chain.setBulletList();
      } else if (copiedFormat.nodeType === 'orderedList') {
        chain.setOrderedList();
      } else if (copiedFormat.nodeType === 'paragraph') {
        chain.setParagraph();
      }

      // Apply text formatting
      chain.unsetAllMarks();
      copiedFormat.marks.forEach((markData: any) => {
        chain.setMark(markData.type, markData.attrs);
      });
    }

    // Execute all commands as single transaction for undo
    chain.run();

    setIsActive(false);
    setCopiedFormat(null);
  }, [editor, copiedFormat]);

  // Selection handling with better timing
  useEffect(() => {
    if (!editor || !isActive || !copiedFormat) return;

    let debounceTimeout: NodeJS.Timeout;
    let lastSelection = { from: 0, to: 0 };

    const handleSelectionUpdate = () => {
      const { from, to } = editor.state.selection;
      
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      if (from !== to && (lastSelection.from !== from || lastSelection.to !== to)) {
        lastSelection = { from, to };
        
        debounceTimeout = setTimeout(() => {
          const currentSelection = editor.state.selection;
          if (currentSelection.from === from && 
              currentSelection.to === to && 
              from !== to) {
            applyFormatSimple(); 
          }
        }, 400);
      }
    };

    editor.on('selectionUpdate', handleSelectionUpdate);
    
    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [editor, isActive, copiedFormat, applyFormatSimple]);

  // Escape key to cancel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isActive) {
        setIsActive(false);
        setCopiedFormat(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive]);

  return (

      <button
      className={`
        btn btn-sm d-flex align-items-center justify-content-center px-2 py-2
        ${isActive 
          ? 'btn-secondary' 
          : 'btn-outline-secondary'
        }
      `}
        onClick={copyFormat}
        title={isActive ? "Select text to apply format (ESC to cancel)" : "Select text to copy format"}
      >
        {!isActive ? <i className="ti ti-paint-off fs-16"></i> : <i className="ti ti-paint fs-16"></i>}
      </button>
  );
};

export default FormatPainterButton;
